import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import config from '../../../config';
import links from '../../../config/links';
import {
    clientType,
    PopupTypes,
    SubscriptionIntervalTypes,
    USER_STATUS_FREE,
    USER_STATUS_ANNUAL,
    USER_STATUS_MONTHLY,
} from '../../utils/constants';
import { PowerFunnelSurfaces, SubscriptionGateways, PowerFunnelGatedFeatures } from '../Analytics/constants';
import * as popupActions from '../../actions/popup-actions';
import { trackEvent } from '../Analytics/TrackedClick';
import { titles, userParams } from '../PopUps/LoginPopUpWrapper/LoginPopUp/utils';
import { showLoginPopUp } from '../../actions/login-popup-actions';
import { getFableticsEntitlement } from '../../utils/user';

const globalPrivilegesUrl = `${config.apiHost}/v1/saml/globalprivileges`;

// Module-level guard so the impression event only fires once per surface per page load,
// matching the behaviour of useGlobalPrivilegesCta.
const trackedImpressionSurfaces = new Set();

export const getCtaState = (user) => {
    const isPowered = !!user?.isPowered;
    const isAnnualSubscription = user?.subscriptionTierType === SubscriptionIntervalTypes.YEAR;
    const isMonthlySubscription = user?.subscriptionTierType === SubscriptionIntervalTypes.MONTH;
    // Powered users without an annual tier — covers Stripe/Apple monthly
    // subscribers AND Club/College/HS SaaS owners who arrive with
    // `subscriptionTierType === null` because Power is granted through their
    // organization rather than a personal subscription. They get the same
    // monthly→annual upgrade pitch.
    const isNonAnnualPowered = isPowered && !isAnnualSubscription;
    const hasPastDueSubscription = !!user?.hasPastDueSubscription;
    const isAppleBilled = user?.subscriptionGateway === SubscriptionGateways.APPLE;
    const fableticsCtaUrl = getFableticsEntitlement(user)?.ctaUrl || null;

    const userStatus = (() => {
        if (!isPowered) return USER_STATUS_FREE;
        if (isAnnualSubscription) return USER_STATUS_ANNUAL;
        return USER_STATUS_MONTHLY;
    })();

    const ctaType = (() => {
        if (hasPastDueSubscription) return 'resume_power';
        if (fableticsCtaUrl) return 'visit_fabletics';
        if (isAnnualSubscription) return 'claim_fabletics';
        if (!isPowered) return 'start_power';
        return 'upgrade_annual';
    })();

    // "Visit Fabletics" is gated on isAnnualSubscription because Fabletics
    // entitlements are annual-only — without that gate, a monthly user with a
    // stray ctaUrl on their account (e.g. Apple-billed monthly subscribers)
    // would short-circuit to "Visit Fabletics" instead of "Upgrade & Save".
    // Annual users without a Fabletics entitlement render the same upgrade
    // layout as monthly Stripe users (per the variant 1 design), so they share
    // the "Upgrade & Save" CTA copy. Free users see "Get Power" rather than
    // the older "Sign Up" placeholder — both control and variant 1 designs
    // use the Power-branded copy. Apple-billed monthly users share the same
    // "Upgrade & Save" copy as Stripe monthly users; the Apple-specific
    // routing happens in handleCtaClick.
    const buttonText = (() => {
        if (hasPastDueSubscription) return 'Resume Power';
        if (isAnnualSubscription && fableticsCtaUrl) return 'Visit Fabletics';
        if (isNonAnnualPowered) return 'Upgrade & Save';

        return 'Get Power';
    })();

    return {
        isPowered,
        isAnnualSubscription,
        isMonthlySubscription,
        isNonAnnualPowered,
        isAppleBilled,
        hasPastDueSubscription,
        userStatus,
        ctaType,
        buttonText,
    };
};

// `variant` is passed in by the rendering banner (which already calls
// useFabletics), rather than re-derived here, to avoid a duplicate Statsig
// experiment lookup further down the tree.
const useFableticsCta = ({
    surface = PowerFunnelSurfaces.HOME,
    variant = 'control',
    trackImpressions = false,
    onDismiss,
    onAppleManageClick,
} = {}) => {
    const user = useSelector(({ auth }) => auth?.user);
    const dispatch = useDispatch();
    const history = useHistory();

    const {
        isPowered,
        isAnnualSubscription,
        isMonthlySubscription,
        isAppleBilled,
        hasPastDueSubscription,
        userStatus,
        ctaType,
        buttonText,
    } = getCtaState(user);
    const fableticsCtaUrl = getFableticsEntitlement(user)?.ctaUrl || null;

    const eventProperties = {
        surface,
        ctaType,
        userSubscriptionStatus: userStatus,
        platform: clientType,
        variant,
    };

    // Wait for `user` to hydrate from Redux before firing — the banner only
    // renders for logged-in eligible users, so a null user here means the
    // store hasn't hydrated yet and `eventProperties` would carry the wrong
    // `userStatus` / `ctaType`. Re-running on `user?.id` and `variant`
    // captures their final values; the Set guard still dedupes across remounts.
    useEffect(() => {
        if (!trackImpressions || !user) {
            return;
        }
        const impressionKey = `fabletics_module_impression_${surface}`;
        if (trackedImpressionSurfaces.has(impressionKey)) {
            return;
        }
        trackEvent('fabletics_module_impression', eventProperties);
        trackedImpressionSurfaces.add(impressionKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackImpressions, surface, user?.id, variant]);

    // Used by the dual-card annual layout, where entitled users see a "Visit
    // Global Privileges" button alongside their Fabletics CTA. Opens the GP SAML
    // URL in a new tab with `noopener,noreferrer` so the opened page can't reach
    // back into this window and the Referer is stripped.
    const handleVisitGlobalPrivilegesClick = (e) => {
        e.stopPropagation();
        trackEvent('fabletics_secondary_cta_click', {
            ...eventProperties,
            ctaType: 'visit_global_privileges',
        });
        window.open(globalPrivilegesUrl, '_blank', 'noopener,noreferrer');
    };

    const handleCtaClick = (e) => {
        e.stopPropagation();
        trackEvent('fabletics_primary_cta_click', eventProperties);

        if (!user) {
            dispatch(showLoginPopUp({
                userParamsNeeded: [userParams.CLAIMED_PROFILE],
                titles: titles.POWER_PERKS,
                type: 'power_perks',
                showCloseIcon: true,
                closeOnClickOutside: true,
            }));
            return;
        }

        if (hasPastDueSubscription) {
            history.push('/subscriptions');
            return;
        }

        // Entitled users → open the server-provided Fabletics activation URL
        // in a new tab. Checked before the Apple-billed guard because clicking
        // "Visit Fabletics" claims an existing perk rather than upgrading the
        // Power subscription, so an Apple-billed monthly user with a Fabletics
        // row should still be able to activate. The URL is fully built by
        // backend (includes code, affinity, vendorId) per ENG-2163 comment
        // #117093 — do not compose it client-side. `noopener,noreferrer`
        // prevents the opened page from reaching back into this window and
        // strips the Referer header so the activation code isn't leaked in
        // logs on Fabletics' side.
        if (fableticsCtaUrl) {
            window.open(fableticsCtaUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        // Apple-billed monthly subscribers can't be upgraded through the web
        // (Apple manages the subscription); the banner shows an info popup
        // explaining where to manage it on iOS / in Apple Settings.
        if (isPowered && isMonthlySubscription && isAppleBilled) {
            if (onAppleManageClick) {
                onAppleManageClick();
            }
            return;
        }

        // Stripe monthly subscribers land on the dedicated monthly → annual
        // popup (the bare Stripe upgrade form) — Fabletics creative
        // deliberately does NOT render there since they've already chosen
        // Power. Free users and tier-null club/college/HS SaaS owners get
        // the V4 popup, which renders the Fabletics creative when the
        // experiment is on (per ENG-2168). Although tier-null users are
        // already powered through their organization, they didn't pick a
        // Power plan personally, so the V4 sign-up-style flow is the right
        // entry point for them rather than the monthly→annual upgrade form.
        const popup = (isPowered && isMonthlySubscription)
            ? PopupTypes.POWER_MONTHLY_UPGRADE_POPUP
            : PopupTypes.UPGRADE_TO_POWER_POPUP;

        dispatch(popupActions.showPopupNew({
            popup,
            referringSurface: surface,
            gatedFeature: PowerFunnelGatedFeatures.FABLETICS,
            onClosePopup: onDismiss,
        }));
    };

    const handleLearnMoreClick = (e) => {
        e.stopPropagation();
        const eventProps = { ...eventProperties, ctaType: 'learn_more' };
        trackEvent('fabletics_secondary_cta_click', eventProps);
        window.open(links.powerPlayersPage, '_blank', 'noopener,noreferrer');
    };

    return {
        buttonText,
        ctaType,
        eventProperties,
        fableticsCtaUrl,
        handleCtaClick,
        handleLearnMoreClick,
        handleVisitGlobalPrivilegesClick,
        hasPastDueSubscription,
        isAnnualSubscription,
        isAppleBilled,
        isMonthlySubscription,
        isPowered,
        user,
        userStatus,
        variant,
    };
};

export default useFableticsCta;
